// oxlint-disable-next-line no-restricted-imports
import { createRoutePath } from "@myvp/shared/src/functions/create-route-path"
import { studyPreviewRouteNames } from "@myvp/study-preview/src/route-names"
import { StudySponsorRolePermission } from "src/gql/graphql"

const studio = "/:vaultId/studio/:globalSponsorStudyId"
const collection = `${studio}/collection/:collectionId`
const settings = `${studio}/settings`
const exports = `${studio}/exports`
const collectionSurveys = `${collection}/surveys`

export const eproModuleRouteNames = {
  studio: createRoutePath(studio),
  studySettings: createRoutePath(settings),
  studyLanguages: createRoutePath(`${settings}/languages`),
  studyResources: createRoutePath(`${settings}/resources`),
  studyDeployments: createRoutePath(`${settings}/deployments`),
  studyDeploymentsBulkDeploy: createRoutePath(
    `${settings}/deployments/bulk-deploy`
  ),
  studyDataChanges: createRoutePath(`${settings}/data-changes`),
  studySiteSignature: createRoutePath(`${settings}/site-signature`),
  studyParticipantId: createRoutePath(`${settings}/participant-id`),
  studyExports: createRoutePath(`${exports}`),
  studyCustomExports: createRoutePath(`${exports}/custom-exports`),
  createCustomExport: createRoutePath(`${exports}/custom-exports/create`),
  editCustomExport: createRoutePath(
    `${exports}/custom-exports/edit/:customExportId`
  ),
  previewCustomExport: createRoutePath(`${exports}/custom-exports/preview`),
  studyExportJobs: createRoutePath(`${exports}/export-jobs`),
  outboundApis: createRoutePath(`${exports}/outbound-apis`),
  complianceViews: createRoutePath(`${exports}/compliance-views`),
  createFtp: createRoutePath(`${exports}/export-jobs/create-ftp`),
  editFtp: createRoutePath(`${exports}/export-jobs/edit-ftp/:exportJobId`),
  createCdb: createRoutePath(`${exports}/export-jobs/create-cdb`),
  editCdb: createRoutePath(`${exports}/export-jobs/edit-cdb/:exportJobId`),
  inPersonAccess: createRoutePath(`${settings}/in-person-access`),
  collections: createRoutePath(`${studio}/collections`),
  collection: createRoutePath(collection),
  collectionStudyDetails: createRoutePath(`${collection}/study/details`),
  collectionScreeningId: createRoutePath(`${collection}/screening-id`),
  collectionSurveys: createRoutePath(collectionSurveys),
  collectionCreateSurvey: createRoutePath(`${collectionSurveys}/create`),
  collectionAddSurveyFromLibrary: createRoutePath(
    `${collectionSurveys}/add-from-library`
  ),
  collectionEditSurvey: createRoutePath(
    `${collectionSurveys}/edit/:collectionPacketId`
  ),
  collectionLanguages: createRoutePath(`${collection}/languages`),
  collectionEvents: createRoutePath(`${collection}/events`),
  collectionGroups: createRoutePath(`${collection}/groups`),
  collectionRules: createRoutePath(`${collection}/rules`),
  createSurveyRule: createRoutePath(`${collection}/rules/survey/create`),
  editSurveyRule: createRoutePath(`${collection}/rules/survey/edit/:ruleId`),
  createCriteriaCheckRule: createRoutePath(
    `${collection}/rules/criteria-check/create`
  ),
  editCriteriaCheckRule: createRoutePath(
    `${collection}/rules/criteria-check/edit/:ruleId`
  ),
  createScheduleTriggerRule: createRoutePath(
    `${collection}/rules/schedule-trigger/create`
  ),
  editScheduleTriggerRule: createRoutePath(
    `${collection}/rules/schedule-trigger/edit/:ruleId`
  ),
  createPopulateEventRule: createRoutePath(
    `${collection}/rules/populate-event/create`
  ),
  editPopulateEventRule: createRoutePath(
    `${collection}/rules/populate-event/edit/:ruleId`
  ),
  createSurveyTriggeredApiRule: createRoutePath(
    `${collection}/rules/survey-triggered-api/create`
  ),
  editSurveyTriggeredApiRule: createRoutePath(
    `${collection}/rules/survey-triggered-api/edit/:ruleId`
  ),

  /**
   * Publically accessible link to the study preview. This link is meant to be shared, so it should be **stable over time**.
   */
  studyPreview: createRoutePath("/study-preview/:collectionId"),

  /**
   * Previewing from edit/create survey
   */
  dynamicEproPreview: createRoutePath("/studio/preview"),
  /**
   * Previewing from surveys list, or collection document
   */
  globalEproPreview: studyPreviewRouteNames.sponsors.globalEproPreview,
  /**
   * Previewing from add from library page
   */
  libraryEproPreview: createRoutePath(`/studio/preview/:surveyTemplateId`),
  /** @deprecated */
  DEPRECATED_eproPreviewV2: createRoutePath(
    `/sponsor/studio/preview/:surveyId`
  ),
  /** @deprecated */
  DEPRECATED_eproPreviewV1: createRoutePath(`/sponsor/epro/preview/:surveyId`),
}

export const permissionsByRouteName: Record<
  keyof typeof eproModuleRouteNames,
  {
    canWrite: StudySponsorRolePermission[] | "all" // "all" corresponds to users with minimal permissions - i.e. any user that can open studio can write on the route
  }
> = {
  studio: {
    canWrite: "all",
  },
  studySettings: {
    canWrite: [StudySponsorRolePermission.StudioStudySettingsWrite],
  },
  studyLanguages: {
    canWrite: [StudySponsorRolePermission.StudioStudySettingsWrite],
  },
  studyResources: {
    canWrite: [StudySponsorRolePermission.StudioStudySettingsWrite],
  },
  studyDeployments: {
    canWrite: [StudySponsorRolePermission.StudioStudySettingsDeploymentsWrite],
  },
  studyDeploymentsBulkDeploy: {
    canWrite: [StudySponsorRolePermission.StudioStudySettingsDeploymentsWrite],
  },
  studyDataChanges: {
    canWrite: [StudySponsorRolePermission.StudioStudySettingsWrite],
  },
  studySiteSignature: {
    canWrite: [StudySponsorRolePermission.StudioStudySettingsWrite],
  },
  studyParticipantId: {
    canWrite: [StudySponsorRolePermission.StudioStudySettingsWrite],
  },
  studyExports: {
    canWrite: "all",
  },
  studyCustomExports: {
    canWrite: "all",
  },
  createCustomExport: {
    canWrite: "all",
  },
  editCustomExport: {
    canWrite: "all",
  },
  previewCustomExport: {
    canWrite: "all",
  },
  studyExportJobs: {
    canWrite: "all",
  },
  outboundApis: {
    canWrite: "all",
  },
  complianceViews: {
    canWrite: "all",
  },
  createFtp: {
    canWrite: "all",
  },
  editFtp: {
    canWrite: "all",
  },
  createCdb: {
    canWrite: "all",
  },
  editCdb: {
    canWrite: "all",
  },
  inPersonAccess: {
    canWrite: [StudySponsorRolePermission.StudioStudySettingsWrite],
  },
  collections: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  collection: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  collectionStudyDetails: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  collectionScreeningId: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  collectionSurveys: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  collectionCreateSurvey: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  collectionAddSurveyFromLibrary: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  collectionEditSurvey: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  collectionLanguages: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  collectionEvents: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  collectionGroups: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  collectionRules: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  createSurveyRule: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  editSurveyRule: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  createCriteriaCheckRule: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  editCriteriaCheckRule: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  createScheduleTriggerRule: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  editScheduleTriggerRule: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  createPopulateEventRule: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  editPopulateEventRule: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  createSurveyTriggeredApiRule: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  editSurveyTriggeredApiRule: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },

  /**
   * Publically accessible link to the study preview. This link is meant to be shared, so it should be **stable over time**.
   */
  studyPreview: {
    canWrite: "all",
  },

  /**
   * Previewing from edit/create survey
   */
  dynamicEproPreview: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  /**
   * Previewing from surveys list, or collection document
   */
  globalEproPreview: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  /**
   * Previewing from add from library page
   */
  libraryEproPreview: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  /** @deprecated */
  DEPRECATED_eproPreviewV2: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
  /** @deprecated */
  DEPRECATED_eproPreviewV1: {
    canWrite: [StudySponsorRolePermission.StudioCollectionWrite],
  },
}

export type StudioRoute =
  (typeof eproModuleRouteNames)[keyof typeof eproModuleRouteNames]["route"]

export const mapRouteToRouteName = (route: StudioRoute) => {
  let targetRouteName: keyof typeof eproModuleRouteNames | null = null
  const routeEntries = Object.entries(eproModuleRouteNames)
  for (const [routeName, routeConfig] of routeEntries) {
    if (routeConfig.route === route) {
      targetRouteName = routeName as keyof typeof eproModuleRouteNames
      break
    }
  }
  return targetRouteName
}
